import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { useLanguage } from '../contexts/LanguageContext';
import { Plane, Hotel, Calendar, User, MapPin, Package, Phone, Mail, Search, Loader2, Users, Download, ChevronDown, ChevronUp, FileText, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { motion } from 'framer-motion';

// Types for our data
type OperationEntry = {
  id: string;
  pilgrimId: string;
  pilgrimName: string;
  passportNumber: string;
  gender: 'male' | 'female';
  residenceCountry: string;
  packageName: string;
  guideId?: string;
  guideName?: string;
  organizerId?: string;
  organizerName?: string;
  operatorId?: string;
  operatorName?: string;
  operatorOrganizerId?: string;
  operatorOrganizerName?: string;
  isOwner?: boolean;
  email?: string;
  mobileNumber?: string;
  nationality?: string;
};

type PilgrimData = {
  id: string;
  Name?: string;
  Passport_Number?: string;
  Email?: string;
  Mobile_Number?: string;
  Residence_Country?: string;
  Nationality_Country?: string;
  package_nickName?: string;
  PNR_NO?: string;
  main_makkah_roomType?: string;
  shifting_makkah_roomType?: string;
  madinah_roomType?: string;
};

type FlightData = {
  id: string;
  pnrNumber: string;
  arrivalDate: string;
  outboundFlightNumber: string;
  outboundDeparture: string;
  outboundArrival: string;
  outboundArrivalTime: string;
  returnFlightNumber: string;
  returnDate: string;
  returnDeparture: string;
  returnDepartureTime: string;
  returnArrival: string;
};

type PackageData = {
  id: string;
  name: string;
  mainMakkahHotel: string;
  mainMakkahStartDate: string;
  mainMakkahEndDate: string;
  shiftingMakkahHotel?: string;
  shiftingMakkahStartDate?: string;
  shiftingMakkahEndDate?: string;
  madinahHotel?: string;
  madinahStartDate?: string;
  madinahEndDate?: string;
};

// Room information type
type RoomInfo = {
  hotelName: string;
  roomNumber: string;
  loading: boolean;
};

type PilgrimWithDetails = OperationEntry & {
  pilgrimData?: PilgrimData;
  flightData?: FlightData;
  packageData?: PackageData;
  roomInfos?: Record<string, RoomInfo>;  // Map hotel name to room info
};

// Group key type to ensure consistent grouping
type GroupKey = string;

const PilgrimsInformationsPage = () => {
  const { translations } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState<string[]>([]);
  const [operationEntries, setOperationEntries] = useState<OperationEntry[]>([]);
  const [pilgrimsData, setPilgrimsData] = useState<Record<string, PilgrimData>>({});
  const [flightsData, setFlightsData] = useState<Record<string, FlightData>>({});
  const [packagesData, setPackagesData] = useState<Record<string, PackageData>>({});
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [pilgrimRooms, setPilgrimRooms] = useState<Record<string, Record<string, string>>>({});
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [isOwnerMode, setIsOwnerMode] = useState(false);
  
  // Check user role
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Function to add a log message with timestamp
  const addLog = (message: string) => {
    const timestamp = new Date().toISOString().split('T')[1].slice(0, 8);
    console.log(`[${timestamp}] ${message}`);
    setLoadingProgress(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Load operation entries and related data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingProgress([]);
        
        addLog("🚀 Starting data fetch process");
        console.time("Total data fetch time");

        // STEP 1: Fetch operation entries based on user role and mode
        addLog("📋 Fetching operation entries");
        console.time("Operation entries fetch time");
        
        let operationQuery;
        if (userRole === 'admin') {
          if (isOwnerMode) {
            // Admin in owner mode - fetch all entries
            operationQuery = query(
              collection(firestore, 'operationlist')
            );
            addLog("👑 Admin mode (owner view): Fetching all entries");
          } else {
            // Admin in operator mode - fetch entries with an operator
            operationQuery = query(
              collection(firestore, 'operationlist'),
              where('operatorId', '!=', null)
            );
            addLog("👑 Admin mode (operator view): Fetching all entries with operators");
          }
        } else if (userRole === 'organizer' && userId) {
          if (isOwnerMode) {
            // Organizer in owner mode - fetch entries where they are the organizer
            operationQuery = query(
              collection(firestore, 'operationlist'),
              where('organizerId', '==', userId)
            );
            addLog(`🏢 Organizer mode (owner view): Fetching entries where organizer ID is: ${userId}`);
          } else {
            // Organizer in operator mode - fetch entries where they are the operator's organizer
            operationQuery = query(
              collection(firestore, 'operationlist'),
              where('operatorOrganizerId', '==', userId)
            );
            addLog(`🏢 Organizer mode (operator view): Fetching entries where operator's organizer ID is: ${userId}`);
          }
        } else {
          throw new Error("Unauthorized access. Only admin and organizer can view this page.");
        }

        const operationSnapshot = await getDocs(operationQuery);
        addLog(`✅ Found ${operationSnapshot.size} operation entries`);
        console.timeEnd("Operation entries fetch time");

        // Filter operation entries based on mode
        const entries = operationSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as OperationEntry))
          .filter(entry => {
            // For operator mode, only include entries with operatorId
            // For owner mode, just include all entries
            return isOwnerMode ? true : entry.operatorId;
          });
          
        addLog(`📊 After filtering, ${entries.length} entries ${isOwnerMode ? 'owned by organizer' : 'have operator information'}`);
        setOperationEntries(entries);
        
        if (entries.length === 0) {
          addLog(`⚠️ No entries ${isOwnerMode ? 'owned by organizer' : 'with operator information'} found`);
          setLoading(false);
          return;
        }

        // STEP 2: Collect unique IDs for batch fetching
        addLog("🔍 Collecting unique IDs for batch fetching");
        
        // Get unique pilgrim IDs
        const pilgrimIds = [...new Set(entries.map(e => e.pilgrimId))];
        addLog(`📊 Found ${pilgrimIds.length} unique pilgrim IDs`);
        
        // Get unique package names
        const packageNames = [...new Set(entries.map(e => e.packageName))];
        addLog(`📊 Found ${packageNames.length} unique package names`);

        // STEP 3: Fetch pilgrim data (which contains PNR numbers)
        addLog("👤 Fetching pilgrim data");
        console.time("Pilgrim data fetch time");
        
        // Process pilgrim IDs in batches (Firestore limit: 30 IDs per "in" query)
        const BATCH_SIZE = 30;
        const pilgrimBatches = [];
        for (let i = 0; i < pilgrimIds.length; i += BATCH_SIZE) {
          pilgrimBatches.push(pilgrimIds.slice(i, i + BATCH_SIZE));
        }
        
        addLog(`📚 Processing ${pilgrimBatches.length} pilgrim batches`);
        
        const pilgrimResults: Record<string, PilgrimData> = {};
        const pnrSet = new Set<string>();
        
        for (let i = 0; i < pilgrimBatches.length; i++) {
          const batch = pilgrimBatches[i];
          addLog(`📦 Processing pilgrim batch ${i+1}/${pilgrimBatches.length} (${batch.length} pilgrims)`);
          
          const pilgrimsQuery = query(
            collection(firestore, 'Pilgrims'),
            where('__name__', 'in', batch)
          );
          
          const pilgrimSnapshot = await getDocs(pilgrimsQuery);
          addLog(`✅ Batch ${i+1}: Found ${pilgrimSnapshot.size} pilgrims`);
          
          pilgrimSnapshot.forEach(doc => {
            const pilgrimData = doc.data() as PilgrimData;
            pilgrimResults[doc.id] = { id: doc.id, ...pilgrimData };
            
            if (pilgrimData.PNR_NO) {
              pnrSet.add(pilgrimData.PNR_NO);
            }
          });
        }
        
        addLog(`✅ Total: Found ${Object.keys(pilgrimResults).length} pilgrims, ${pnrSet.size} unique PNRs`);
        setPilgrimsData(pilgrimResults);
        console.timeEnd("Pilgrim data fetch time");

        // STEP 4: Fetch flight data using PNR numbers
        addLog("✈️ Fetching flight data");
        console.time("Flight data fetch time");
        
        // Process PNR numbers in batches
        const pnrArray = [...pnrSet];
        const pnrBatches = [];
        for (let i = 0; i < pnrArray.length; i += BATCH_SIZE) {
          pnrBatches.push(pnrArray.slice(i, i + BATCH_SIZE));
        }
        
        addLog(`📚 Processing ${pnrBatches.length} PNR batches`);
        
        const flightResults: Record<string, FlightData> = {};
        
        for (let i = 0; i < pnrBatches.length; i++) {
          const batch = pnrBatches[i];
          addLog(`📦 Processing PNR batch ${i+1}/${pnrBatches.length} (${batch.length} PNRs)`);
          
          const flightsQuery = query(
            collection(firestore, 'Flights'),
            where('pnrNumber', 'in', batch)
          );
          
          const flightSnapshot = await getDocs(flightsQuery);
          addLog(`✅ Batch ${i+1}: Found ${flightSnapshot.size} flights`);
          
          flightSnapshot.forEach(doc => {
            const flightData = doc.data() as FlightData;
            // Index by PNR for easier lookup
            flightResults[flightData.pnrNumber] = { id: doc.id, ...flightData };
          });
        }
        
        addLog(`✅ Total: Found ${Object.keys(flightResults).length} flights`);
        setFlightsData(flightResults);
        console.timeEnd("Flight data fetch time");

        // STEP 5: Fetch package data
        addLog("📦 Fetching package data");
        console.time("Package data fetch time");
        
        // Process package names in batches
        const packageBatches = [];
        for (let i = 0; i < packageNames.length; i += BATCH_SIZE) {
          packageBatches.push(packageNames.slice(i, i + BATCH_SIZE));
        }
        
        addLog(`📚 Processing ${packageBatches.length} package batches`);
        
        const packageResults: Record<string, PackageData> = {};
        
        for (let i = 0; i < packageBatches.length; i++) {
          const batch = packageBatches[i];
          addLog(`📦 Processing package batch ${i+1}/${packageBatches.length} (${batch.length} packages)`);
          
          const packagesQuery = query(
            collection(firestore, 'Packages'),
            where('name', 'in', batch)
          );
          
          const packageSnapshot = await getDocs(packagesQuery);
          addLog(`✅ Batch ${i+1}: Found ${packageSnapshot.size} packages`);
          
          packageSnapshot.forEach(doc => {
            const packageData = doc.data() as PackageData;
            // Index by package name for easier lookup
            packageResults[packageData.name] = { id: doc.id, ...packageData };
          });
        }
        
        addLog(`✅ Total: Found ${Object.keys(packageResults).length} packages`);
        setPackagesData(packageResults);
        console.timeEnd("Package data fetch time");

        // All data fetched successfully
        addLog("✅ All data fetched successfully");
        console.timeEnd("Total data fetch time");
        
      } catch (error: any) {
        console.error("Error fetching data:", error);
        setError(`Error: ${error.message}`);
        addLog(`❌ Error: ${error.message}`);
        toast.error(`Error: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userRole, userId, isOwnerMode]); // Added isOwnerMode as a dependency to re-fetch when toggled
  
  // Combine all data to create full pilgrim records with details
  const pilgrimsWithDetails = useMemo(() => {
    console.time("Combining data");
    addLog("🔄 Combining operation entries with pilgrim, flight, and package data");
    
    const result = operationEntries.map(entry => {
      const pilgrimData = pilgrimsData[entry.pilgrimId];
      let flightData = undefined;
      
      if (pilgrimData?.PNR_NO) {
        flightData = flightsData[pilgrimData.PNR_NO];
      }
      
      const packageData = packagesData[entry.packageName];
      
      return {
        ...entry,
        pilgrimData,
        flightData,
        packageData
      };
    });
    
    addLog(`✅ Combined ${result.length} records with full details`);
    console.timeEnd("Combining data");
    return result;
  }, [operationEntries, pilgrimsData, flightsData, packagesData]);
  
  // Create lookup map for hotel-to-pilgrim associations
  const hotelPilgrimMap = useMemo(() => {
    const map: Record<string, string[]> = {};
    
    pilgrimsWithDetails.forEach(pilgrim => {
      if (pilgrim.packageData) {
        // Add pilgrim to main Makkah hotel
        const mainMakkahHotel = pilgrim.packageData.mainMakkahHotel;
        if (mainMakkahHotel && !map[mainMakkahHotel]) {
          map[mainMakkahHotel] = [];
        }
        if (mainMakkahHotel) {
          map[mainMakkahHotel].push(pilgrim.pilgrimId);
        }
        
        // Add pilgrim to shifting Makkah hotel if exists
        const shiftingMakkahHotel = pilgrim.packageData.shiftingMakkahHotel;
        if (shiftingMakkahHotel && !map[shiftingMakkahHotel]) {
          map[shiftingMakkahHotel] = [];
        }
        if (shiftingMakkahHotel) {
          map[shiftingMakkahHotel].push(pilgrim.pilgrimId);
        }
        
        // Add pilgrim to Madinah hotel if exists
        const madinahHotel = pilgrim.packageData.madinahHotel;
        if (madinahHotel && !map[madinahHotel]) {
          map[madinahHotel] = [];
        }
        if (madinahHotel) {
          map[madinahHotel].push(pilgrim.pilgrimId);
        }
      }
    });
    
    console.log("🏨 Created hotel-pilgrim lookup map:", Object.keys(map).length, "hotels");
    return map;
  }, [pilgrimsWithDetails]);
  
  // Fetch room numbers from accommodation tables
  useEffect(() => {
    const fetchRoomNumbers = async () => {
      if (Object.keys(hotelPilgrimMap).length === 0 || loadingRooms) return;
      
      setLoadingRooms(true);
      console.log("🔍 Starting room number fetch process");
      
      try {
        const hotelNames = Object.keys(hotelPilgrimMap);
        console.log(`🏨 Processing ${hotelNames.length} hotels`);
        
        // Create a map to store results: pilgrimId -> { hotelName -> roomNumber }
        const roomMap: Record<string, Record<string, string>> = {};
        
        // Process hotels in sequence to avoid overwhelming Firestore
        for (let i = 0; i < hotelNames.length; i++) {
          const hotelName = hotelNames[i];
          const pilgrimIds = hotelPilgrimMap[hotelName];
          
          console.log(`🏨 Processing hotel ${i+1}/${hotelNames.length}: ${hotelName} (${pilgrimIds.length} pilgrims)`);
          
          // Find accommodation tables for this hotel
          const tablesQuery = query(
            collection(firestore, 'AccommodationTables'),
            where('hn', '==', hotelName)
          );
          
          const tablesSnapshot = await getDocs(tablesQuery);
          console.log(`📊 Found ${tablesSnapshot.size} tables for hotel: ${hotelName}`);
          
          // Process each table to find pilgrim assignments
          for (const tableDoc of tablesSnapshot.docs) {
            const tableData = tableDoc.data();
            
            // Skip if no rooms data
            if (!tableData.r || !Array.isArray(tableData.r)) continue;
            
            // Process each room
            for (const room of tableData.r) {
              // Skip if no beds data
              if (!room.b || !Array.isArray(room.b)) continue;
              
              // Process each bed
              for (const bed of room.b) {
                // Skip if no assignments
                if (!bed.a || !Array.isArray(bed.a)) continue;
                
                // Check each assignment
                for (const assignment of bed.a) {
                  const pilgrimId = assignment.pid;
                  
                  // If this pilgrim is in our list
                  if (pilgrimIds.includes(pilgrimId)) {
                    // Create entry in map if it doesn't exist
                    if (!roomMap[pilgrimId]) {
                      roomMap[pilgrimId] = {};
                    }
                    
                    // Add room number for this hotel
                    roomMap[pilgrimId][hotelName] = room.n.toString();
                  }
                }
              }
            }
          }
        }
        
        console.log(`✅ Room lookup complete, found rooms for ${Object.keys(roomMap).length} pilgrims`);
        setPilgrimRooms(roomMap);
      } catch (error) {
        console.error("Error fetching room numbers:", error);
      } finally {
        setLoadingRooms(false);
      }
    };
    
    fetchRoomNumbers();
  }, [hotelPilgrimMap]);

  // Group pilgrims by PNR and package
  const groupedPilgrims = useMemo(() => {
    console.time("Grouping pilgrims");
    addLog("🔄 Grouping pilgrims by flight and package");
    
    const groups: Record<string, PilgrimWithDetails[]> = {};
    
    for (const pilgrim of pilgrimsWithDetails) {
      const pnr = pilgrim.pilgrimData?.PNR_NO || 'unknown';
      const packageName = pilgrim.packageName || 'unknown';
      const key = `${pnr}|${packageName}`;
      
      if (!groups[key]) {
        groups[key] = [];
      }
      
      // Add room information to the pilgrim object
      const pilgrimWithRooms = {
        ...pilgrim,
        roomInfos: {} as Record<string, RoomInfo>
      };
      
      // Add room info for each hotel in the pilgrim's package
      if (pilgrim.packageData) {
        const hotels = [
          pilgrim.packageData.mainMakkahHotel,
          pilgrim.packageData.shiftingMakkahHotel,
          pilgrim.packageData.madinahHotel
        ].filter(Boolean) as string[];
        
        hotels.forEach(hotel => {
          const roomNumber = pilgrimRooms[pilgrim.pilgrimId]?.[hotel] || "";
          pilgrimWithRooms.roomInfos![hotel] = {
            hotelName: hotel,
            roomNumber: roomNumber || "",
            loading: !roomNumber && loadingRooms
          };
        });
      }
      
      groups[key].push(pilgrimWithRooms);
    }
    
    // Sort pilgrims within each group
    for (const key in groups) {
      groups[key].sort((a, b) => a.pilgrimName.localeCompare(b.pilgrimName));
    }
    
    addLog(`✅ Created ${Object.keys(groups).length} groups of pilgrims`);
    console.timeEnd("Grouping pilgrims");
    return groups;
  }, [pilgrimsWithDetails, pilgrimRooms, loadingRooms]);
  
  // Convert grouped object to array for sorting
  const sortedGroups = useMemo(() => {
    console.time("Sorting groups");
    addLog("🔄 Sorting groups by flight date and package");
    
    // Convert to array of [key, pilgrims] pairs
    const groupArray = Object.entries(groupedPilgrims).map(([key, pilgrims]) => ({
      key,
      pilgrims,
      // Get representative pilgrim for sorting
      rep: pilgrims[0]
    }));
    
    // Sort by flight arrival date and then by package
    groupArray.sort((a, b) => {
      // Get representative pilgrims
      const pilgrimA = a.rep;
      const pilgrimB = b.rep;
      
      // Sort by arrival date
      const dateA = pilgrimA.flightData?.arrivalDate || '';
      const dateB = pilgrimB.flightData?.arrivalDate || '';
      
      if (dateA !== dateB) {
        // Convert dd/mm/yyyy to sortable format
        const [dayA, monthA, yearA] = (dateA || '01/01/1970').split('/');
        const [dayB, monthB, yearB] = (dateB || '01/01/1970').split('/');
        
        const dateObjA = new Date(`${yearA}-${monthA}-${dayA}`);
        const dateObjB = new Date(`${yearB}-${monthB}-${dayB}`);
        
        return dateObjA.getTime() - dateObjB.getTime();
      }
      
      // If dates are equal, sort by package name
      return pilgrimA.packageName.localeCompare(pilgrimB.packageName);
    });
    
    addLog(`✅ Sorted ${groupArray.length} groups`);
    console.timeEnd("Sorting groups");
    return groupArray;
  }, [groupedPilgrims]);
  
  // Filter groups by search term
  const filteredGroups = useMemo(() => {
    if (!searchTerm.trim()) return sortedGroups;
    
    const term = searchTerm.toLowerCase().trim();
    
    // Filter groups where any pilgrim matches the search
    return sortedGroups.filter(group => 
      group.pilgrims.some(pilgrim => 
        pilgrim.pilgrimName?.toLowerCase().includes(term) ||
        pilgrim.passportNumber?.toLowerCase().includes(term) ||
        pilgrim.residenceCountry?.toLowerCase().includes(term) ||
        pilgrim.packageName?.toLowerCase().includes(term) ||
        pilgrim.operatorName?.toLowerCase().includes(term) ||
        pilgrim.operatorOrganizerName?.toLowerCase().includes(term) ||
        pilgrim.pilgrimData?.PNR_NO?.toLowerCase().includes(term) ||
        pilgrim.pilgrimData?.Email?.toLowerCase().includes(term) ||
        pilgrim.pilgrimData?.Mobile_Number?.toLowerCase().includes(term)
      )
    );
  }, [sortedGroups, searchTerm]);
  
  const toggleGroupExpand = (key: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Handle toggle mode switch
  const handleModeToggle = () => {
    setIsOwnerMode(!isOwnerMode);
    // Reset expanded groups when switching modes
    setExpandedGroups({});
  };
  
  // Export to Excel function
  const exportToExcel = () => {
    try {
      // Prepare data for export
      const data = pilgrimsWithDetails.map(pilgrim => {
        // Determine pilgrim's relationship with operator
        let operatorStatus = '';
        if (isOwnerMode) {
          operatorStatus = pilgrim.isOwner === false ? 'Not Owner' : 'Owner';
        } else {
          if (pilgrim.operatorOrganizerId === pilgrim.organizerId) {
            operatorStatus = pilgrim.isOwner === false ? 'Not Owner and Operator' : 'Owner and Operator';
          } else {
            operatorStatus = 'Operator Only';
          }
        }
        
        return {
          'Pilgrim Name': pilgrim.pilgrimName,
          'Passport Number': pilgrim.passportNumber,
          'Email': pilgrim.pilgrimData?.Email || '',
          'Mobile Number': pilgrim.pilgrimData?.Mobile_Number || '',
          'Residence Country': pilgrim.residenceCountry,
          'Nationality': pilgrim.pilgrimData?.Nationality_Country || '',
          'Package': pilgrim.packageName,
          'Status': operatorStatus,
          'Operator': pilgrim.operatorName || '',
          'Operator Organization': pilgrim.operatorOrganizerName || '',
          'Guide': pilgrim.guideName || '',
          'Organizer': pilgrim.organizerName || '',
          'PNR': pilgrim.pilgrimData?.PNR_NO || '',
          'Flight From': pilgrim.flightData?.outboundDeparture || '',
          'Flight To': pilgrim.flightData?.outboundArrival || '',
          'Arrival Date': pilgrim.flightData?.arrivalDate || '',
          'Arrival Time': pilgrim.flightData?.outboundArrivalTime || '',
          'Return Date': pilgrim.flightData?.returnDate || '',
          'Return Time': pilgrim.flightData?.returnDepartureTime || '',
          'Main Makkah Hotel': pilgrim.packageData?.mainMakkahHotel || '',
          'Main Makkah Room Type': pilgrim.pilgrimData?.main_makkah_roomType || '',
          'Main Makkah Room': pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData?.mainMakkahHotel || ''] || 'N/A',
          'Main Makkah Dates': pilgrim.packageData ? 
            `${pilgrim.packageData.mainMakkahStartDate} - ${pilgrim.packageData.mainMakkahEndDate}` : '',
          'Shifting Makkah Hotel': pilgrim.packageData?.shiftingMakkahHotel || '',
          'Shifting Makkah Room Type': pilgrim.pilgrimData?.shifting_makkah_roomType || '',
          'Shifting Makkah Room': pilgrim.packageData?.shiftingMakkahHotel ? 
            (pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.shiftingMakkahHotel] || 'N/A') : '',
          'Shifting Makkah Dates': pilgrim.packageData?.shiftingMakkahStartDate && pilgrim.packageData?.shiftingMakkahEndDate ?
            `${pilgrim.packageData.shiftingMakkahStartDate} - ${pilgrim.packageData.shiftingMakkahEndDate}` : '',
          'Madinah Hotel': pilgrim.packageData?.madinahHotel || '',
          'Madinah Room Type': pilgrim.pilgrimData?.madinah_roomType || '',
          'Madinah Room': pilgrim.packageData?.madinahHotel ? 
            (pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.madinahHotel] || 'N/A') : '',
          'Madinah Dates': pilgrim.packageData?.madinahStartDate && pilgrim.packageData?.madinahEndDate ?
            `${pilgrim.packageData.madinahStartDate} - ${pilgrim.packageData.madinahEndDate}` : '',
        };
      });
      
      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 30 }, // Pilgrim Name
        { wch: 15 }, // Passport Number
        { wch: 30 }, // Email
        { wch: 15 }, // Mobile Number
        { wch: 15 }, // Residence Country
        { wch: 15 }, // Nationality
        { wch: 15 }, // Package
        { wch: 20 }, // Status
        { wch: 25 }, // Operator
        { wch: 25 }, // Operator Organization
        { wch: 25 }, // Guide
        { wch: 25 }, // Organizer
        { wch: 10 }, // PNR
        { wch: 20 }, // Flight From
        { wch: 20 }, // Flight To
        { wch: 12 }, // Arrival Date
        { wch: 12 }, // Arrival Time
        { wch: 12 }, // Return Date
        { wch: 12 }, // Return Time
        { wch: 25 }, // Main Makkah Hotel
        { wch: 15 }, // Main Makkah Room Type
        { wch: 10 }, // Main Makkah Room
        { wch: 25 }, // Main Makkah Dates
        { wch: 25 }, // Shifting Makkah Hotel
        { wch: 15 }, // Shifting Makkah Room Type
        { wch: 10 }, // Shifting Makkah Room
        { wch: 25 }, // Shifting Makkah Dates
        { wch: 25 }, // Madinah Hotel
        { wch: 15 }, // Madinah Room Type
        { wch: 10 }, // Madinah Room
        { wch: 25 }, // Madinah Dates
      ];
      
      // Create workbook and add worksheet
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Pilgrims Information');
      
      // Generate filename with date and mode
      const date = new Date().toISOString().split('T')[0];
      const mode = isOwnerMode ? 'owner' : 'operator';
      const filename = `pilgrims-information-${mode}-${date}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success('Exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with search, mode toggle, and export */}
      <div className="bg-white p-6 rounded-xl shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {translations?.pilgrimsInformations?.title || 'Pilgrims Informations'}
          </h1>
          <p className="text-gray-500 mt-1">
            {isOwnerMode 
              ? (translations?.pilgrimsInformations?.subtitleOwner || 'View detailed information about pilgrims you own')
              : (translations?.pilgrimsInformations?.subtitle || 'View detailed information about pilgrims with operators')}
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Mode Toggle */}
          {(userRole === 'admin' || userRole === 'organizer') && (
            <button
              onClick={handleModeToggle}
              disabled={loading}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors border ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              } ${
                isOwnerMode 
                  ? 'bg-blue-50 text-blue-600 border-blue-200' 
                  : 'bg-gray-50 text-gray-600 border-gray-200'
              }`}
            >
              {isOwnerMode ? (
                <>
                  <ToggleRight size={20} />
                  <span>Owner View</span>
                </>
              ) : (
                <>
                  <ToggleLeft size={20} />
                  <span>Operator View</span>
                </>
              )}
            </button>
          )}
          
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder={translations?.pilgrimsInformations?.search || "Search pilgrims..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
          </div>
          
          {/* Export Button */}
          <button 
            onClick={exportToExcel}
            disabled={loading || pilgrimsWithDetails.length === 0}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
              loading || pilgrimsWithDetails.length === 0
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-500 text-white hover:bg-primary-600'
            }`}
          >
            <Download size={18} />
            <span>{translations?.pilgrimsInformations?.export || 'Export'}</span>
          </button>
        </div>
      </div>
      
      {/* View Mode Banner */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-3 rounded-lg text-sm flex items-center justify-between ${
          isOwnerMode 
            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
            : 'bg-gray-50 text-gray-700 border border-gray-200'
        }`}
      >
        <div className="flex items-center gap-2">
          {isOwnerMode ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
          <span>
            {isOwnerMode 
              ? (translations?.pilgrimsInformations?.ownerModeInfo || 'Showing pilgrims that you own')
              : (translations?.pilgrimsInformations?.operatorModeInfo || 'Showing pilgrims with assigned operators')}
          </span>
        </div>
        <button 
          onClick={handleModeToggle}
          className={`text-sm px-3 py-1 rounded ${
            isOwnerMode 
              ? 'bg-blue-100 text-blue-700 hover:bg-blue-200' 
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          {isOwnerMode 
            ? (translations?.pilgrimsInformations?.switchToOperator || 'Switch to Operator View')
            : (translations?.pilgrimsInformations?.switchToOwner || 'Switch to Owner View')}
        </button>
      </motion.div>
      
      {/* Loading State */}
      {loading && (
        <div className="bg-white p-6 rounded-xl shadow-sm">
          <div className="flex items-center gap-4 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <div className="text-gray-700">
              {isOwnerMode 
                ? (translations?.pilgrimsInformations?.loadingOwnerMode || 'Loading pilgrim information in owner mode...')
                : (translations?.pilgrimsInformations?.loading || 'Loading pilgrim information...')}
            </div>
          </div>
        </div>
      )}
      
      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-50 text-red-700 p-6 rounded-xl shadow-sm">
          <div className="font-medium mb-2">Error loading data:</div>
          <div>{error}</div>
        </div>
      )}
      
      {/* Empty State */}
      {!loading && !error && pilgrimsWithDetails.length === 0 && (
        <div className="bg-white p-12 rounded-xl shadow-sm text-center">
          <div className="text-gray-400 mb-4">
            <Users size={48} className="mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700">
              {isOwnerMode 
                ? (translations?.pilgrimsInformations?.noPilgrimsOwner || 'No pilgrims found that you own')
                : (translations?.pilgrimsInformations?.noPilgrims || 'No pilgrims with operators found')}
            </h3>
          </div>
        </div>
      )}
      
      {/* Main Content - Table View */}
      {!loading && !error && filteredGroups.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {/* Main Table for groups */}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.pilgrimsInformations?.flightInfo || 'Flight Information'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.pilgrimsInformations?.accommodationInfo || 'Accommodation Information'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.pilgrimsInformations?.packageInfo || 'Package'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.pilgrimsInformations?.pilgrims || 'Pilgrims'}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {translations?.pilgrimsInformations?.actions || 'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredGroups.map((group, groupIndex) => {
                const { key, rep, pilgrims } = group;
                const isExpanded = expandedGroups[key] || false;
                
                return (
                  <React.Fragment key={key}>
                    {/* Main group row */}
                    <tr className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{groupIndex + 1}</td>
                      
                      {/* Flight Information */}
                      <td className="px-4 py-4 text-sm">
                        {rep.flightData ? (
                          <div className="space-y-1">
                            <div className="font-medium text-gray-800 flex items-center gap-1">
                              <Plane size={16} className="text-primary-500" />
                              PNR: {rep.flightData.pnrNumber}
                            </div>
                            <div>
                              <span className="text-gray-600">Outbound:</span>{' '}
                              <span>{rep.flightData.outboundDeparture} → {rep.flightData.outboundArrival} ({rep.flightData.outboundFlightNumber})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar size={14} className="text-gray-500" />
                              {rep.flightData.arrivalDate}, {rep.flightData.outboundArrivalTime}
                            </div>
                            <div className="mt-1">
                              <span className="text-gray-600">Return:</span>{' '}
                              <span>{rep.flightData.returnDeparture} → {rep.flightData.returnArrival} ({rep.flightData.returnFlightNumber})</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Calendar size={14} className="text-gray-500" />
                              {rep.flightData.returnDate}, {rep.flightData.returnDepartureTime}
                            </div>
                          </div>
                        ) : (
                          <div className="text-yellow-600">
                            PNR: {rep.pilgrimData?.PNR_NO || 'Not available'}
                            <br />
                            No flight information found
                          </div>
                        )}
                      </td>
                      
                      {/* Accommodation Information */}
                      <td className="px-4 py-4 text-sm">
                        {rep.packageData ? (
                          <div className="space-y-2">
                            <div>
                              <div className="font-medium flex items-center gap-1">
                                <Hotel size={16} className="text-primary-500" />
                                {rep.packageData.mainMakkahHotel}
                               
                              </div>
                              <div className="flex flex-col">
                                <span className="capitalize text-gray-600">
                                  {rep.pilgrimData?.main_makkah_roomType || 'Room type not specified'}
                                </span>
                                <span className="text-gray-600 flex items-center gap-1">
                                  <Calendar size={14} className="text-gray-500" />
                                  {rep.packageData.mainMakkahStartDate} - {rep.packageData.mainMakkahEndDate}
                                </span>
                              </div>
                            </div>
                            
                            {rep.packageData.shiftingMakkahHotel && (
                              <div>
                                <div className="font-medium flex items-center gap-1">
                                  <Hotel size={16} className="text-primary-500" />
                                  {rep.packageData.shiftingMakkahHotel}
                                 
                                </div>
                                <div className="flex flex-col">
                                  <span className="capitalize text-gray-600">
                                    {rep.pilgrimData?.shifting_makkah_roomType || 'Room type not specified'}
                                  </span>
                                  {rep.packageData.shiftingMakkahStartDate && rep.packageData.shiftingMakkahEndDate && (
                                    <span className="text-gray-600 flex items-center gap-1">
                                      <Calendar size={14} className="text-gray-500" />
                                      {rep.packageData.shiftingMakkahStartDate} - {rep.packageData.shiftingMakkahEndDate}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {rep.packageData.madinahHotel && (
                              <div>
                                <div className="font-medium flex items-center gap-1">
                                  <Hotel size={16} className="text-primary-500" />
                                  {rep.packageData.madinahHotel}
                                 
                                </div>
                                <div className="flex flex-col">
                                  <span className="capitalize text-gray-600">
                                    {rep.pilgrimData?.madinah_roomType || 'Room type not specified'}
                                  </span>
                                  {rep.packageData.madinahStartDate && rep.packageData.madinahEndDate && (
                                    <span className="text-gray-600 flex items-center gap-1">
                                      <Calendar size={14} className="text-gray-500" />
                                      {rep.packageData.madinahStartDate} - {rep.packageData.madinahEndDate}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="text-gray-500">No accommodation information</div>
                        )}
                      </td>
                      
                      {/* Package */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          <Package size={16} className="text-primary-500" />
                          <span className="font-medium">{rep.packageName}</span>
                        </div>
                      </td>
                      
                      {/* Pilgrims Count */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1">
                          <Users size={16} className="text-primary-500" />
                          <span className="font-medium">{pilgrims.length} pilgrims</span>
                        </div>
                      </td>
                      
                      {/* Actions */}
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-center">
                        <button
                          onClick={() => toggleGroupExpand(key)}
                          className="p-2 rounded-full hover:bg-gray-100"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>
                      </td>
                    </tr>
                    
                    {/* Expanded pilgrims table */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} className="px-0 py-0 border-b">
                          <div className="bg-gray-50 px-4 py-4">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-100">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-14">#</th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {translations?.pilgrimsInformations?.pilgrimInfo || 'Pilgrim Information'}
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {translations?.pilgrimsInformations?.contactInfo || 'Contact Information'}
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {isOwnerMode 
                                      ? (translations?.pilgrimsInformations?.guideInfo || 'Guide Information')
                                      : (translations?.pilgrimsInformations?.operatorInfo || 'Operator Information')}
                                  </th>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    {translations?.pilgrimsInformations?.roomInfo || 'Room Information'}
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-100">
                                {pilgrims.map((pilgrim, index) => {
                                  // Determine pilgrim's relationship with operator and organizer
                                  let statusBadge;
                                  
                                  if (isOwnerMode) {
                                    // In owner mode, just show ownership status
                                    statusBadge = pilgrim.isOwner === false ? (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                                        Not Owner
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                        Owner
                                      </span>
                                    );
                                  } else {
                                    // In operator mode, show the relationship
                                    if (pilgrim.operatorOrganizerId === pilgrim.organizerId) {
                                      if (pilgrim.isOwner === false) {
                                        statusBadge = (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 mt-1">
                                            Not Owner & Operator
                                          </span>
                                        );
                                      } else {
                                        statusBadge = (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                                            Owner & Operator
                                          </span>
                                        );
                                      }
                                    } else {
                                      statusBadge = (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                                          Operator Only
                                        </span>
                                      );
                                    }
                                  }
                                  
                                  return (
                                    <tr key={pilgrim.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{index + 1}</td>
                                      
                                      {/* Pilgrim Information */}
                                      <td className="px-4 py-3 text-sm">
                                        <div className="flex items-center gap-3">
                                          <div className={`p-1.5 rounded-full ${pilgrim.gender === 'male' ? 'bg-blue-100' : 'bg-pink-100'}`}>
                                            <User 
                                              className={pilgrim.gender === 'male' ? 'text-blue-600' : 'text-pink-600'} 
                                              size={16} 
                                            />
                                          </div>
                                          <div>
                                            <div className="font-medium">{pilgrim.pilgrimName}</div>
                                            <div className="text-xs text-gray-500">
                                              {translations?.pilgrimsInformations?.passport || 'Passport'}: {pilgrim.passportNumber}
                                            </div>
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                              <MapPin size={12} className="text-gray-400" />
                                              {pilgrim.residenceCountry}
                                              {pilgrim.pilgrimData?.Nationality_Country && pilgrim.pilgrimData.Nationality_Country !== pilgrim.residenceCountry && (
                                                <span className="text-gray-400">
                                                  (Nationality: {pilgrim.pilgrimData.Nationality_Country})
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </td>
                                      
                                      {/* Contact Information */}
                                      <td className="px-4 py-3 text-sm">
                                        <div className="space-y-1">
                                          {pilgrim.pilgrimData?.Email && (
                                            <div className="flex items-center gap-1">
                                              <Mail size={14} className="text-gray-500" />
                                              <a 
                                                href={`mailto:${pilgrim.pilgrimData.Email}`}
                                                className="text-primary-600 hover:underline"
                                              >
                                                {pilgrim.pilgrimData.Email}
                                              </a>
                                            </div>
                                          )}
                                          
                                          {pilgrim.pilgrimData?.Mobile_Number && (
                                            <div className="flex items-center gap-2">
                                              <Phone size={14} className="text-gray-500" />
                                              <span>{pilgrim.pilgrimData.Mobile_Number}</span>
                                              <a
                                                href={`https://wa.me/${pilgrim.pilgrimData.Mobile_Number.replace(/\D/g, '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1 bg-green-100 text-green-700 rounded-full hover:bg-green-200"
                                                title="WhatsApp"
                                              >
                                                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
                                                </svg>
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                      
                                      {/* Guide/Operator Information with Status */}
                                      <td className="px-4 py-3 text-sm">
                                        {isOwnerMode ? (
                                          // Owner mode - show guide info
                                          <div>
                                            {pilgrim.guideName ? (
                                              <>
                                                <div className="font-medium">{pilgrim.guideName}</div>
                                                {pilgrim.organizerName && (
                                                  <div className="text-xs text-gray-500">
                                                    {translations?.pilgrimsInformations?.organization || 'Organization'}: {pilgrim.organizerName}
                                                  </div>
                                                )}
                                                {/* Ownership status badge */}
                                                {statusBadge}
                                              </>
                                            ) : (
                                              <div className="text-gray-400">No guide assigned</div>
                                            )}
                                          </div>
                                        ) : (
                                          // Operator mode - show operator info
                                          <div>
                                            {pilgrim.operatorName ? (
                                              <>
                                                <div className="font-medium">{pilgrim.operatorName}</div>
                                                {pilgrim.operatorOrganizerName && (
                                                  <div className="text-xs text-gray-500">
                                                    {translations?.pilgrimsInformations?.organization || 'Organization'}: {pilgrim.operatorOrganizerName}
                                                  </div>
                                                )}
                                                {/* Operator status badge */}
                                                {statusBadge}
                                                
                                                {/* Pilgrim guide and organizer info */}
                                                {pilgrim.guideName && (
                                                  <div className="text-xs text-gray-500 mt-1">
                                                    {translations?.pilgrimsInformations?.guide || 'Guide'}: {pilgrim.guideName}
                                                  </div>
                                                )}
                                                
                                                {pilgrim.organizerName && (
                                                  <div className="text-xs text-gray-500">
                                                    {translations?.pilgrimsInformations?.pilgrimOrganizer || 'Pilgrim Organizer'}: {pilgrim.organizerName}
                                                  </div>
                                                )}
                                              </>
                                            ) : (
                                              <div className="text-gray-400">No operator assigned</div>
                                            )}
                                          </div>
                                        )}
                                      </td>
                                      
                                      {/* Room Information */}
                                      <td className="px-4 py-3 text-sm">
                                        {pilgrim.packageData ? (
                                          <div className="space-y-1">
                                            <div className="flex items-start gap-1">
                                              <span className="text-gray-500 min-w-24">Main Makkah:</span>
                                              {loadingRooms && !pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.mainMakkahHotel] ? (
                                                <span className="text-gray-500 italic">Loading...</span>
                                              ) : pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.mainMakkahHotel] ? (
                                                <span className="font-medium">Room {pilgrimRooms[pilgrim.pilgrimId][pilgrim.packageData.mainMakkahHotel]}</span>
                                              ) : (
                                                <span className="text-yellow-600">Not Accommodated</span>
                                              )}
                                            </div>
                                            
                                            {pilgrim.packageData.shiftingMakkahHotel && (
                                              <div className="flex items-start gap-1">
                                                <span className="text-gray-500 min-w-24">Shifting Makkah:</span>
                                                {loadingRooms && !pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.shiftingMakkahHotel] ? (
                                                  <span className="text-gray-500 italic">Loading...</span>
                                                ) : pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.shiftingMakkahHotel] ? (
                                                  <span className="font-medium">Room {pilgrimRooms[pilgrim.pilgrimId][pilgrim.packageData.shiftingMakkahHotel]}</span>
                                                ) : (
                                                  <span className="text-yellow-600">Not Accommodated</span>
                                                )}
                                              </div>
                                            )}
                                            
                                            {pilgrim.packageData.madinahHotel && (
                                              <div className="flex items-start gap-1">
                                                <span className="text-gray-500 min-w-24">Madinah:</span>
                                                {loadingRooms && !pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.madinahHotel] ? (
                                                  <span className="text-gray-500 italic">Loading...</span>
                                                ) : pilgrimRooms[pilgrim.pilgrimId]?.[pilgrim.packageData.madinahHotel] ? (
                                                  <span className="font-medium">Room {pilgrimRooms[pilgrim.pilgrimId][pilgrim.packageData.madinahHotel]}</span>
                                                ) : (
                                                  <span className="text-yellow-600">Not Accommodated</span>
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="text-gray-500">No package information</div>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      
      {/* Empty Search Results */}
      {!loading && !error && pilgrimsWithDetails.length > 0 && filteredGroups.length === 0 && (
        <div className="bg-white p-12 rounded-xl shadow-sm text-center">
          <div className="text-gray-400 mb-4">
            <Search size={48} className="mx-auto mb-4" />
            <h3 className="text-xl font-medium text-gray-700">
              {translations?.pilgrimsInformations?.noSearchResults || 'No pilgrims match your search'}
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
              {translations?.pilgrimsInformations?.tryAdjustingSearch || 
                'Try adjusting your search criteria or clear the search field to see all pilgrims.'}
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 px-4 py-2 bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200"
            >
              {translations?.pilgrimsInformations?.clearSearch || 'Clear Search'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilgrimsInformationsPage;
